import { useMutation } from '@apollo/client/react';
import { Alert, Modal, Tooltip, Typography } from 'antd';
import { CopyOutlined, LikeOutlined } from '@ant-design/icons';
import copy from 'copy-to-clipboard';

import SectionMessage from 'components/SectionMessage/SectionMessage';

import useAnalytics from 'hooks/useAnalytics';
import useCustomerInfo from 'hooks/useCustomerInfo';
import useTimeoutValue from 'hooks/useTimeoutValue';

import { INITIATE_DOCUMENT_VERIFICATION } from 'mutations/documentVerification';

import { ANALYTICS_EVENTS, SIZE, STATUS } from 'utilities/constants';

const DocVModal = ({ open, ...modalProps }) => {
  const { customerInfo } = useCustomerInfo();
  const { trackEvent } = useAnalytics();
  const [linkCopied, setLinkCopied] = useTimeoutValue(false, 2000);

  const [initiateDocV, { data, loading, error, reset }] = useMutation(
    INITIATE_DOCUMENT_VERIFICATION,
    {
      onCompleted: () => {
        trackEvent(ANALYTICS_EVENTS.DOCV_INITIATED, {
          customerId: customerInfo.id,
        });
      },
    },
  );

  const docVUrl = data?.initiateDocumentVerification?.url;

  const handleCopyLink = () => {
    if (!docVUrl) return;
    copy(docVUrl);
    setLinkCopied(true);
  };

  const handleInitiate = () => {
    if (!customerInfo) return;
    reset();
    const address = customerInfo.address;

    initiateDocV({
      variables: {
        input: {
          customerId: customerInfo.id,
          firstName: customerInfo.firstName,
          lastName: customerInfo.lastName,
          dateOfBirth: customerInfo.dateOfBirth,
          address: {
            street1: address.street1,
            street2: address.street2,
            city: address.city,
            state: address.state,
            zipCode: address.postalCode,
            country: 'US',
          },
        },
      },
    }).catch(() => {});
  };

  return (
    <Modal
      {...modalProps}
      title="Socure Document Verification"
      open={open}
      onOk={handleInitiate}
      okText="Generate DocV Link"
      confirmLoading={loading}
      okButtonProps={{
        style: { display: docVUrl ? 'none' : undefined },
      }}
      cancelText="Close"
      afterClose={reset}
      width={480}
    >
      <div
        style={{
          position: 'relative',
          minHeight: '120px',
          marginTop: 'var(--spacing-xl)',
          marginBottom: 'var(--spacing-xl)',
        }}
      >
        <SectionMessage
          status={STATUS.SUCCESS}
          size={SIZE.SM}
          cover={true}
          visible={!!docVUrl}
          text="Link Generated"
          buttons={[
            {
              text: (
                <Tooltip
                  title={
                    linkCopied ? (
                      <span>
                        Link Copied <LikeOutlined />
                      </span>
                    ) : (
                      'Copy Link'
                    )
                  }
                >
                  <span>
                    <CopyOutlined /> Copy DocV Link
                  </span>
                </Tooltip>
              ),
              type: 'primary',
              onClick: handleCopyLink,
              'data-testid': 'copy-docv-link-button',
            },
          ]}
        />
        <Typography.Paragraph>
          Generate a secure link to send to the customer for document
          verification. The customer will use this link to upload their ID for
          verification through Socure.
        </Typography.Paragraph>
        {error && (
          <Alert
            banner
            type="error"
            title="Failed to generate DocV link. Please try again."
            showIcon
            data-testid="docv-error-alert"
          />
        )}
      </div>
    </Modal>
  );
};

export default DocVModal;
